import { getRandomInt } from "../Shared/Helpers.tsx";
import {
    HistogramQuestionOptions,
    HistogramSpec,
    ProblemData,
    ProblemFormatOptions,
} from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";
import { PrintedLanguage, printedLanguageOf } from "./PrintedLanguage.ts";
import { histogramContexts as enContexts, histogramPrompts as en } from "../Files/Prompts/en/histograms.ts";
import { histogramContexts as esContexts, histogramPrompts as es } from "../Files/Prompts/es/histograms.ts";
import { histogramContexts as frContexts, histogramPrompts as fr } from "../Files/Prompts/fr/histograms.ts";

const PROMPTS: Record<PrintedLanguage, typeof en> = { en, es, fr };
const CONTEXTS: Record<PrintedLanguage, typeof enContexts> = { en: enContexts, es: esContexts, fr: frContexts };

/**
 * Histograms (ADR 0099 §2). A histogram is a bar graph of intervals: each bar
 * covers a closed run of whole numbers, written `70-79` with an en dash, and
 * its height is how many values fall in it. Like a bar graph (ADR 0040) one
 * histogram is asked several questions: every question in a group carries the
 * same `problemGroup.histogram`, which `PdfGenerator/Page.tsx` draws once above
 * the group's rows.
 *
 * The rule this file exists to enforce is the one ADR 0022 §1a sets: the sheet
 * asks only what the drawing shows exactly. A bar covers an interval, so no
 * question asks for a single value, the median, the mean or the range. What is
 * asked is how many values are in one interval, how many are at or above an
 * interval's lower end, how many there are in all, and which interval has the
 * most. Every rule on the data is met by how it is built, never by drawing at
 * random and checking:
 *
 * - Five or six bars of equal width, the width and the first interval coming
 *   from the context. Labels are the closed whole-number intervals, so no
 *   value could belong to two bars.
 * - Every count is a whole number of grid steps (1 or 2), so every bar ends on
 *   a gridline (ADR 0039 §3). The axis tops out at the tallest bar.
 * - The tallest bar is drawn first and every other bar is kept below it, so
 *   exactly one interval has the most.
 * - At most one bar is empty, never the first or the last, so the data
 *   plainly spans the axis.
 * - The counts add to between 15 and 40.
 */

/** A group is three questions about one histogram (ADR 0099 §4). */
export const HISTOGRAM_QUESTIONS_PER_CHART = 3;

const MIN_TOTAL = 15;
const MAX_TOTAL = 40;

/** The en dash that joins an interval's ends. It is in WinAnsi, so Helvetica draws it. */
const EN_DASH = "–";

/** `70` and `79` as the bar's label, `70-79` with an en dash. */
export function intervalLabel(low: number, high: number): string {
    return `${low}${EN_DASH}${high}`;
}

/** The question types a sheet draws from, in rotation order. */
export function histogramQuestionTypes(question: HistogramQuestionOptions): HistogramQuestionOptions[] {
    if (question === HistogramQuestionOptions.Mixed) {
        return [
            HistogramQuestionOptions.OneInterval,
            HistogramQuestionOptions.AtLeast,
            HistogramQuestionOptions.Total,
            HistogramQuestionOptions.Most,
        ];
    }
    return [question];
}

/**
 * Question types with exactly one true answer per histogram. A sheet pinned to
 * one of them has only one question to ask about each, so it draws a histogram
 * per question, as a pinned bar-graph `Total` sheet does, and `perPageOptions.ts`
 * offers it its own, lower, counts (ADR 0050 §3).
 */
export const SINGLE_ANSWER_HISTOGRAM_QUESTIONS: HistogramQuestionOptions[] = [
    HistogramQuestionOptions.Total,
    HistogramQuestionOptions.Most,
];

export function sumOf(counts: number[]): number {
    return counts.reduce((sum, count) => sum + count, 0);
}

/**
 * A count per bar that meets the rules above by construction, and the scale
 * it is drawn at. `bins` is 5 or 6.
 *
 * The tallest bar is at most 6 grid steps high on a step-1 axis and 5 on a
 * step-2 axis, so the axis has at most seven labelled gridlines and a half
 * page can still draw them apart.
 */
export function buildCounts(bins: number): { counts: number[]; gridStep: number; maxValue: number } {
    const gridStep = getRandomInt(1, 2);
    const tallest = gridStep === 1 ? getRandomInt(5, 6) : getRandomInt(3, 5);
    const minUnits = Math.ceil(MIN_TOTAL / gridStep);
    const maxUnits = Math.floor(MAX_TOTAL / gridStep);

    const modeAt = getRandomInt(0, bins - 1);
    // Half of all histograms have one empty bar, an inside one that is not the tallest.
    let emptyAt = -1;
    if (getRandomInt(0, 1) === 1) {
        const inside = Array.from({ length: bins - 2 }, (_, i) => i + 1).filter((i) => i !== modeAt);
        emptyAt = inside[getRandomInt(0, inside.length - 1)];
    }

    // In grid steps. Every bar but the tallest and the empty one holds at least
    // one step and at most one fewer than the tallest.
    const units = Array.from({ length: bins }, (_, i) => {
        if (i === modeAt) return tallest;
        if (i === emptyAt) return 0;
        return getRandomInt(1, tallest - 1);
    });

    const total = () => sumOf(units);
    const others = units.map((_, i) => i).filter((i) => i !== modeAt && i !== emptyAt);
    // Both loops terminate: the least a histogram can hold is `tallest` plus
    // one step in each other bar, at most 6 + 5, and the most is `tallest` plus
    // `tallest - 1` in each other bar, at least 3 + 3 * 2 = 9 steps, which is
    // the 8 a step-2 axis needs for 15 (ADR 0099 §2).
    while (total() < minUnits) {
        const raisable = others.filter((i) => units[i] < tallest - 1);
        units[raisable[getRandomInt(0, raisable.length - 1)]]++;
    }
    while (total() > maxUnits) {
        const lowerable = others.filter((i) => units[i] > 1);
        units[lowerable[getRandomInt(0, lowerable.length - 1)]]--;
    }

    return { counts: units.map((unit) => unit * gridStep), gridStep, maxValue: tallest * gridStep };
}

/** A freshly drawn histogram, with the words the prompts use for it. */
function buildHistogram(
    language: PrintedLanguage,
    usedTitles: Set<string>
): { spec: HistogramSpec; noun: string; unit: string } {
    const all = CONTEXTS[language];
    // Two histograms on one page about the same thing read as a mistake, so a
    // page uses each context once while it has unused ones left.
    const fresh = all.filter((context) => !usedTitles.has(context.title));
    const contexts = fresh.length > 0 ? fresh : all;
    const context = contexts[getRandomInt(0, contexts.length - 1)];
    usedTitles.add(context.title);

    const start = context.starts[getRandomInt(0, context.starts.length - 1)];
    const fitting = [5, 6].filter((bins) => start + bins * context.width - 1 <= context.limit);
    const bins = fitting[getRandomInt(0, fitting.length - 1)];

    const lows = Array.from({ length: bins }, (_, i) => start + i * context.width);
    const highs = lows.map((low) => low + context.width - 1);
    const { counts, gridStep, maxValue } = buildCounts(bins);
    return {
        spec: {
            title: context.title,
            labels: lows.map((low, i) => intervalLabel(low, highs[i])),
            lows,
            highs,
            counts,
            gridStep,
            maxValue,
        },
        noun: context.noun,
        unit: context.unit,
    };
}

/** What a group has already asked about its histogram, so the next question is new (ADR 0040 §3). */
interface GroupUsage {
    once: Set<HistogramQuestionOptions>;
    intervals: Set<number>;
    thresholds: Set<number>;
}

function tryBuildQuestion(
    type: HistogramQuestionOptions,
    spec: HistogramSpec,
    words: { noun: string; unit: string },
    used: GroupUsage,
    language: PrintedLanguage
): { prompt: string; answer: string; commit: () => void } | null {
    const t = PROMPTS[language];
    const { counts, labels, lows, highs } = spec;
    const { noun, unit } = words;

    if (SINGLE_ANSWER_HISTOGRAM_QUESTIONS.includes(type) && used.once.has(type)) {
        return null;
    }
    const commitOnce = () => { used.once.add(type); };

    switch (type) {
        case HistogramQuestionOptions.Total:
            return { prompt: t.total(noun), answer: String(sumOf(counts)), commit: commitOnce };
        case HistogramQuestionOptions.Most:
            return {
                prompt: t.most(noun),
                answer: labels[counts.indexOf(Math.max(...counts))],
                commit: commitOnce,
            };
        case HistogramQuestionOptions.AtLeast: {
            // From a bar's lower end, never the first bar's (that would be everything).
            const available = counts.map((_, i) => i).filter((i) => i > 0 && !used.thresholds.has(i));
            if (available.length === 0) return null;
            const i = available[getRandomInt(0, available.length - 1)];
            return {
                prompt: t.atLeast(noun, lows[i], unit),
                answer: String(sumOf(counts.slice(i))),
                commit: () => used.thresholds.add(i),
            };
        }
        default: {
            // OneInterval.
            const available = counts.map((_, i) => i).filter((i) => !used.intervals.has(i));
            if (available.length === 0) return null;
            const i = available[getRandomInt(0, available.length - 1)];
            return {
                prompt: t.oneInterval(noun, lows[i], highs[i], unit),
                answer: String(counts[i]),
                commit: () => used.intervals.add(i),
            };
        }
    }
}

/**
 * One histogram and up to `want` distinct questions about it, all tagged with
 * the same group so the page draws it once. Comes back short when the allowed
 * types run out of new questions for this histogram (a pinned `Total` sheet has
 * one per histogram); the caller draws a fresh one for the rest.
 * `startTypeIndex` carries a Mixed sheet's rotation across groups, for the
 * reason `buildBarGraphGroup` gives. Three consecutive types out of four are
 * three different types.
 */
export function buildHistogramGroup(
    question: HistogramQuestionOptions,
    want: number,
    groupKey: string,
    startKey: number,
    startTypeIndex: number,
    language: PrintedLanguage,
    usedTitles: Set<string> = new Set()
): { problems: ProblemData[]; nextKey: number; nextTypeIndex: number } {
    const types = histogramQuestionTypes(question);
    const { spec, noun, unit } = buildHistogram(language, usedTitles);
    const used: GroupUsage = { once: new Set(), intervals: new Set(), thresholds: new Set() };
    const problems: ProblemData[] = [];
    let key = startKey;
    let typeIndex = startTypeIndex;
    let stalled = 0;

    while (problems.length < want && stalled < types.length) {
        const type = types[typeIndex % types.length];
        typeIndex++;
        const picked = tryBuildQuestion(type, spec, { noun, unit }, used, language);
        if (!picked) {
            stalled++;
            continue;
        }
        stalled = 0;
        picked.commit();
        problems.push({
            key: key++,
            numbers: [],
            operator: "",
            display: [picked.prompt, picked.answer],
            histogramQuestion: type,
            problemGroup: { key: groupKey, format: ProblemFormatOptions.Histograms, histogram: spec },
        });
    }

    return { problems, nextKey: key, nextTypeIndex: typeIndex % types.length };
}

/** A page-shaped set of histogram problems, three questions to a histogram. */
export function generateHistogramPages(settings: ISettingsProps): ProblemData[][] | null {
    const question = settings.HistogramQuestion ?? HistogramQuestionOptions.Mixed;
    const types = histogramQuestionTypes(question);
    const singleAnswer = SINGLE_ANSWER_HISTOGRAM_QUESTIONS.includes(question);
    // Whole histograms: a count carried over from another format, such as 4,
    // would leave one with a single question (ADR 0099 §4). A sheet pinned to
    // a one-answer question draws a histogram a question, so it has no such unit.
    const count = settings.NumberOfProblemsPerPage;
    const perPage = singleAnswer
        ? count
        : Math.max(HISTOGRAM_QUESTIONS_PER_CHART, count - (count % HISTOGRAM_QUESTIONS_PER_CHART));
    const language = printedLanguageOf(settings);
    const pages: ProblemData[][] = [];
    let nextKey = 1;

    for (let page = 0; page < settings.NumberOfPages; page++) {
        const problems: ProblemData[] = [];
        let groupIndex = 0;
        // A Mixed page starts its rotation anywhere, so a page of one
        // histogram does not always leave out the same question type.
        let typeIndex = types.length > 1 ? getRandomInt(0, types.length - 1) : 0;
        const usedTitles = new Set<string>();
        // Every group yields at least one question about a fresh histogram, so
        // this always makes progress; the guard turns a broken guarantee into a
        // short page rather than a hung tab.
        const guardLimit = perPage * 4 + 10;
        let guard = 0;

        while (problems.length < perPage && guard < guardLimit) {
            guard++;
            const want = Math.min(HISTOGRAM_QUESTIONS_PER_CHART, perPage - problems.length);
            const group = buildHistogramGroup(
                question,
                want,
                `histogram:${page}:${groupIndex}`,
                nextKey,
                typeIndex,
                language,
                usedTitles
            );
            if (group.problems.length === 0) {
                break;
            }
            nextKey = group.nextKey;
            typeIndex = group.nextTypeIndex;
            problems.push(...group.problems);
            groupIndex++;
        }

        pages.push(problems);
    }

    return pages.some((page) => page.length > 0) ? pages : null;
}
